import { useCallback } from "react";
import {
  BidStatus,
  BidStatusTransitionTypeDto,
  SourcingEventStatus,
  type BidQualificationDto,
  type BuyerBidDetailDto,
  useGetSourcingEventQuery,
  useSetBidQualificationMutation,
} from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { hasTenderStageQualification } from "../../sourcing-events/buyer/wizard/utils/procedure";
import { getBidQualificationProgress } from "./bidQualification";

export interface BidQualificationDecision {
  applies: boolean;
  decision: BidQualificationDto | null;
  decisionOpen: boolean;
  reopenedAfterJuryDecision: boolean;
  qualificationRoundOpen: boolean;
  pending: number;
  assessmentRequired: boolean;
  decide: (qualified: boolean, exclusionGroundsAssessment?: string) => Promise<boolean>;
  isLoading: boolean;
}

export const useBidQualificationDecision = (detail: BuyerBidDetailDto): BidQualificationDecision => {
  const { trigger } = useApiError();
  const [setQualification, { isLoading }] = useSetBidQualificationMutation();
  const { data: event } = useGetSourcingEventQuery({ eventId: detail.bid.forEvent });

  const eventId = detail.bid.forEvent;
  const bidId = detail.bid.id;

  const decide = useCallback(
    async (qualified: boolean, exclusionGroundsAssessment?: string) => {
      try {
        await setQualification({
          eventId,
          bidId,
          setBidQualificationRequestDto: { qualified, exclusionGroundsAssessment },
        }).unwrap();
        return true;
      } catch (e) {
        trigger(e);
        return false;
      }
    },
    [setQualification, eventId, bidId, trigger],
  );

  const progress = getBidQualificationProgress(detail);
  const reopenedAfterJuryDecision = detail.qualificationReopenedAfterJuryDecision ?? false;

  return {
    applies:
      detail.bid.status === BidStatus.Submitted &&
      hasTenderStageQualification(
        event?.eventContext?.type === "Public" ? event.eventContext.procedureType : undefined,
      ),
    decision: detail.qualification ?? null,
    decisionOpen:
      event?.status === SourcingEventStatus.Published ||
      (!!event?.status && EVALUATION_STATUSES.includes(event.status) && hasRestoredRejection(detail)),
    reopenedAfterJuryDecision,
    qualificationRoundOpen: event?.status === SourcingEventStatus.Published,
    pending: progress.hasRequirements ? progress.pending : 0,
    assessmentRequired: detail.exclusionGroundsAssessmentRequired ?? false,
    decide,
    isLoading,
  };
};

const EVALUATION_STATUSES: SourcingEventStatus[] = [SourcingEventStatus.Evaluation, SourcingEventStatus.Evaluated];

const hasRestoredRejection = (detail: BuyerBidDetailDto): boolean =>
  detail.bid.statusHistory
    ?.filter(
      (step) =>
        step.type === BidStatusTransitionTypeDto.Rejected || step.type === BidStatusTransitionTypeDto.RejectionRevoked,
    )
    .at(-1)?.type === BidStatusTransitionTypeDto.RejectionRevoked;
